/**
 * Statistics Demo Page
 * Demonstrates the animated statistics and counting system
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AnimatedContainer } from '../lib/animations';
import AnimatedStatistics, { statisticsPresets } from '../components/AnimatedStatistics';
import StaggeredStatistics, { staggerPresets } from '../components/StaggeredStatistics';
import AnimatedProgressBar, { MultiProgressBar } from '../components/AnimatedProgressBar';
import AnimatedIcon, { iconAnimationPresets } from '../components/AnimatedIcon';
import AchievementShowcase from '../components/AchievementShowcase';
import { 
  Home, Award, Users, Calendar, TrendingUp, Star, CheckCircle, Target, 
  Zap, Heart, Building, Trophy, Clock, ThumbsUp, MapPin, Wrench 
} from 'lucide-react';

const StatisticsDemo: React.FC = () => {
  const [selectedDemo, setSelectedDemo] = useState<'basic' | 'staggered' | 'progress' | 'icons' | 'achievements'>('basic');

  // Extended company statistics
  const companyStats = [
    {
      id: 'homes-built',
      label: 'Homes Built',
      value: 250,
      suffix: '+',
      icon: Home,
      color: '#3b82f6',
      description: 'Quality homes delivered to families',
      category: 'Production',
      priority: 1,
      maxValue: 300,
      showProgressBar: true,
      iconAnimation: { ...iconAnimationPresets.achievement, delay: 0.5 }
    },
    {
      id: 'years-experience',
      label: 'Years Experience',
      value: 15,
      suffix: '+',
      icon: Calendar,
      color: '#10b981',
      description: 'Building excellence since 2009',
      category: 'Experience',
      priority: 2,
      maxValue: 20,
      showProgressBar: true,
      iconAnimation: { ...iconAnimationPresets.countingSync, intensity: 'strong' }
    },
    {
      id: 'happy-families',
      label: 'Happy Families',
      value: 240,
      suffix: '+',
      icon: Users,
      color: '#f59e0b',
      description: 'Living their dream homes',
      category: 'Satisfaction',
      priority: 3,
      maxValue: 300,
      showProgressBar: true,
      iconAnimation: { ...iconAnimationPresets.interactive, type: 'heartbeat' }
    },
    {
      id: 'awards',
      label: 'Awards Won',
      value: 12,
      icon: Award,
      color: '#ef4444',
      description: 'Industry recognition and excellence',
      category: 'Recognition',
      priority: 4,
      maxValue: 15,
      showProgressBar: true,
      iconAnimation: { ...iconAnimationPresets.achievement, type: 'tada' }
    },
    {
      id: 'completion-rate',
      label: 'On-Time Completion',
      value: 98.5,
      suffix: '%',
      decimals: 1,
      icon: CheckCircle,
      color: '#10b981',
      description: 'Projects delivered on schedule',
      category: 'Performance',
      priority: 5,
      maxValue: 100,
      showProgressBar: true
    },
    {
      id: 'satisfaction',
      label: 'Customer Satisfaction',
      value: 4.9,
      suffix: '/5',
      decimals: 1,
      icon: Star,
      color: '#f59e0b',
      description: 'Average customer rating',
      category: 'Satisfaction',
      priority: 6,
      maxValue: 5,
      showProgressBar: true
    },
    {
      id: 'locations',
      label: 'Service Areas',
      value: 8,
      icon: MapPin,
      color: '#8b5cf6',
      description: 'Cities and regions served',
      category: 'Coverage',
      priority: 7,
      maxValue: 10,
      showProgressBar: true
    },
    {
      id: 'team-size',
      label: 'Team Members',
      value: 45,
      suffix: '+',
      icon: Building,
      color: '#06b6d4',
      description: 'Skilled professionals',
      category: 'Team',
      priority: 8,
      maxValue: 50,
      showProgressBar: true
    }
  ];

  // Progress bar examples
  const progressBars = [
    {
      id: 'project-completion',
      label: 'Current Projects Completion',
      value: 75,
      fillColor: '#3b82f6',
      showLabel: true,
      labelPosition: 'outside' as const,
      animationDuration: 2,
      striped: true,
      animated: true
    },
    {
      id: 'customer-satisfaction',
      label: 'Customer Satisfaction Score',
      value: 92,
      fillColor: '#10b981',
      gradientColors: ['#10b981', '#34d399'],
      showLabel: true,
      labelPosition: 'inside' as const,
      animationDuration: 2.5,
      glowEffect: true
    },
    {
      id: 'quality-rating',
      label: 'Quality Rating',
      value: 88,
      fillColor: '#f59e0b',
      showLabel: true,
      labelPosition: 'top' as const,
      animationDuration: 1.8,
      pulseOnComplete: true
    },
    {
      id: 'on-time-delivery',
      label: 'On-Time Delivery Rate',
      value: 96,
      fillColor: '#ef4444',
      showLabel: true,
      labelPosition: 'bottom' as const,
      animationDuration: 2.2,
      height: 16,
      striped: true
    }
  ];

  // Icon animation examples
  const iconExamples = [
    { icon: Home, preset: 'achievement', label: 'Achievement' },
    { icon: Trophy, preset: 'interactive', label: 'Interactive' },
    { icon: Zap, preset: 'loading', label: 'Loading' },
    { icon: Heart, preset: 'notification', label: 'Notification' },
    { icon: Target, preset: 'success', label: 'Success' },
    { icon: Wrench, preset: 'countingSync', label: 'Counting Sync' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <AnimatedContainer className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Animated Statistics System
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Showcase of counting animations, progress bars, and interactive statistics
            with smooth transitions and engaging visual effects.
          </p>
          
          {/* Demo Selector */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {[
              { key: 'basic', label: 'Basic Statistics' },
              { key: 'staggered', label: 'Staggered Animation' },
              { key: 'progress', label: 'Progress Bars' },
              { key: 'icons', label: 'Icon Animations' },
              { key: 'achievements', label: 'Achievements' }
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setSelectedDemo(key as any)}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                  selectedDemo === key
                    ? 'bg-blue-600 text-white shadow-lg transform scale-105'
                    : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 shadow-md'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </AnimatedContainer>

        {/* Demo Content */}
        <AnimatedContainer>
          {selectedDemo === 'basic' && (
            <div className="space-y-16">
              {/* Basic Statistics */}
              <section>
                <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
                  Company Statistics
                </h2>
                <AnimatedStatistics
                  statistics={statisticsPresets.companyStats}
                  config={{
                    title: 'Our Track Record',
                    subtitle: 'Numbers that speak to our commitment to excellence',
                    layout: 'grid',
                    animationStyle: 'sequential',
                    countingDuration: 2.5,
                    staggerDelay: 0.3,
                    showProgressBars: true,
                    iconAnimation: true
                  }}
                />
              </section>

              {/* Project Statistics */}
              <section>
                <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
                  Project Performance
                </h2>
                <AnimatedStatistics
                  statistics={statisticsPresets.projectStats}
                  config={{
                    layout: 'row',
                    animationStyle: 'simultaneous',
                    countingDuration: 3,
                    showProgressBars: true,
                    iconAnimation: true
                  }}
                />
              </section>
            </div>
          )}

          {selectedDemo === 'staggered' && (
            <div className="space-y-16">
              {/* Sequential Stagger */}
              <section>
                <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
                  Sequential Animation
                </h2>
                <StaggeredStatistics
                  statistics={companyStats.slice(0, 4)}
                  config={staggerPresets.companyShowcase}
                  title="Company Showcase"
                  subtitle="Watch our achievements unfold one by one"
                  layout="grid"
                />
              </section>

              {/* Center-Out Stagger */}
              <section>
                <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
                  Center-Out Animation
                </h2>
                <StaggeredStatistics
                  statistics={companyStats.slice(4, 8)}
                  config={staggerPresets.achievementUnlock}
                  title="Achievement Unlock"
                  subtitle="Expanding from the center outward"
                  layout="grid"
                />
              </section>

              {/* Priority-Based Stagger */}
              <section>
                <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
                  Priority-Based Animation
                </h2>
                <StaggeredStatistics
                  statistics={companyStats}
                  config={staggerPresets.performanceMetrics}
                  title="Performance Metrics"
                  subtitle="Ordered by importance and impact"
                  layout="grid"
                />
              </section>
            </div>
          )}

          {selectedDemo === 'progress' && (
            <div className="space-y-16">
              {/* Individual Progress Bars */}
              <section>
                <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
                  Individual Progress Bars
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                  {progressBars.map((bar, index) => (
                    <motion.div
                      key={bar.id}
                      className="bg-white p-6 rounded-xl shadow-lg"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.2, duration: 0.5 }}
                    >
                      <AnimatedProgressBar
                        config={bar}
                        className="w-full"
                        triggerOnScroll={true}
                        threshold={0.3}
                      />
                    </motion.div>
                  ))}
                </div>
              </section>

              {/* Multi Progress Bar */}
              <section>
                <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
                  Comparative Progress Bars
                </h2>
                <div className="bg-white p-8 rounded-xl shadow-lg max-w-4xl mx-auto">
                  <MultiProgressBar
                    bars={progressBars}
                    staggerDelay={0.3}
                    showComparison={true}
                    triggerOnScroll={true}
                    threshold={0.2}
                  />
                </div>
              </section>
            </div>
          )}

          {selectedDemo === 'icons' && (
            <div className="space-y-16">
              {/* Icon Animation Showcase */}
              <section>
                <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
                  Icon Animation Presets
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 max-w-4xl mx-auto">
                  {iconExamples.map(({ icon, preset, label }, index) => (
                    <motion.div
                      key={label}
                      className="bg-white p-6 rounded-xl shadow-lg text-center"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1, duration: 0.5 }}
                    >
                      <div className="mb-4 flex justify-center">
                        <AnimatedIcon
                          icon={icon}
                          config={{
                            ...iconAnimationPresets[preset as keyof typeof iconAnimationPresets],
                            trigger: 'immediate',
                            delay: index * 0.2
                          }}
                          size={32}
                          color="#3b82f6"
                          backgroundColor="#3b82f620"
                          borderRadius={8}
                          padding={12}
                        />
                      </div>
                      <h3 className="text-sm font-medium text-gray-900">
                        {label}
                      </h3>
                    </motion.div>
                  ))}
                </div>
              </section>

              {/* Interactive Icons */}
              <section>
                <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
                  Interactive Icons
                </h2>
                <div className="bg-white p-8 rounded-xl shadow-lg max-w-2xl mx-auto">
                  <p className="text-center text-gray-600 mb-6">
                    Hover over the icons to see interactive animations
                  </p>
                  <div className="flex justify-center space-x-8">
                    {[Home, Trophy, Heart, Star, Target].map((Icon, index) => (
                      <AnimatedIcon
                        key={index}
                        icon={Icon}
                        config={{
                          type: 'bounce',
                          trigger: 'hover',
                          intensity: 'normal',
                          duration: 0.5
                        }}
                        size={40}
                        color="#3b82f6"
                        backgroundColor="#f3f4f6"
                        borderRadius={12}
                        padding={16}
                        className="cursor-pointer hover:bg-blue-50 transition-colors duration-200"
                      />
                    ))}
                  </div>
                </div>
              </section>
            </div>
          )}

          {selectedDemo === 'achievements' && (
            <div className="space-y-16">
              {/* Achievement Showcase */}
              <section>
                <AchievementShowcase
                  title="Company Achievements"
                  subtitle="Our awards, certifications, and milestones showcase our commitment to excellence"
                  defaultView="combined"
                  showFilters={true}
                  showViewToggle={true}
                  triggerOnScroll={false}
                />
              </section>
            </div>
          )}
        </AnimatedContainer>

        {/* Footer */}
        <AnimatedContainer className="text-center mt-16">
          <p className="text-gray-600">
            This demo showcases the animated statistics system with counting animations,
            progress bars, and interactive icons. All animations respect user preferences
            for reduced motion and include performance monitoring.
          </p>
        </AnimatedContainer>
      </div>
    </div>
  );
};

export default StatisticsDemo;